/**
 Copyright Highway9 Networks Inc.
 */
import { PayloadAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice, type EntityState } from "@reduxjs/toolkit";
import { RootState } from "..";
import { AlertProfile, defaultOutputPluginTemplate, OutputPluginTemplate } from "~/types/AlertProfile";
import { alertService, AlertTemplateQuery } from "~/services/alert-service";
import { AlertGroup } from "~/types/AlertGroup";
import { createSocketPatchReducer } from "../events/socketPatch";

export const alertProfilesAdapter = createEntityAdapter<AlertProfile>();
export const alertTemplatesAdapter = createEntityAdapter<AlertGroup>({
  selectId: (template) => template.alertType,
});
export const outputPluginTemplatesAdapter = createEntityAdapter<OutputPluginTemplate>({
  selectId: (template) => template.type,
});

interface AlertProfileState {
  open: boolean;
  selectedId: string | null;
  profilesData: EntityState<AlertProfile>;
  templatesData: EntityState<AlertGroup>;
  outputPluginTemplatesData: EntityState<OutputPluginTemplate>;
}

const initialState: AlertProfileState = {
  open: false,
  selectedId: null,
  profilesData: alertProfilesAdapter.getInitialState(),
  templatesData: alertTemplatesAdapter.getInitialState(),
  outputPluginTemplatesData: outputPluginTemplatesAdapter.setAll(
    outputPluginTemplatesAdapter.getInitialState(),
    defaultOutputPluginTemplate,
  ),
};

export const fetchAlertProfiles = createAsyncThunk("alertProfile/fetchAlertProfiles", async () => {
  const data = await alertService.getAlertProfiles();
  return data;
});

export const fetchAlertTemplates = createAsyncThunk("alertProfile/fetchAlertTemplates", async (query?: AlertTemplateQuery) => {
  const data = await alertService.getAlertTemplates(query);
  return data;
});

export const fetchAlertOutputPluginTemplates = createAsyncThunk("alertProfile/fetchAlertOutputPluginTemplates", async () => {
  const data = await alertService.getAlertOutputPluginTemplates();
  return data;
});

const alertProfileSlice = createSlice({
  name: "alertProfile",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(alertProfilesAdapter, (s) => s.profilesData),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAlertProfiles.fulfilled, (state, action) => {
      alertProfilesAdapter.setAll(state.profilesData, action.payload);
    });
    builder.addCase(fetchAlertTemplates.fulfilled, (state, action) => {
      alertTemplatesAdapter.setAll(state.templatesData, action.payload);
    });
    builder.addCase(fetchAlertOutputPluginTemplates.fulfilled, (state, action) => {
      outputPluginTemplatesAdapter.setAll(state.outputPluginTemplatesData, action.payload);
    });
  },
});

export const alertProfileActions = alertProfileSlice.actions;
export default alertProfileSlice.reducer;

const alertProfileEntitySelectors = alertProfilesAdapter.getSelectors(
  (state: RootState) => state.alertProfile.profilesData,
);
const alertTemplateEntitySelectors = alertTemplatesAdapter.getSelectors(
  (state: RootState) => state.alertProfile.templatesData,
);
const outputPluginTemplateEntitySelectors = outputPluginTemplatesAdapter.getSelectors(
  (state: RootState) => state.alertProfile.outputPluginTemplatesData,
);

export const alertProfileData = alertProfileEntitySelectors.selectAll;
export const alertProfileEntities = alertProfileEntitySelectors.selectEntities;

export const alertProfileState = createSelector(
  [(state: RootState) => state.alertProfile.selectedId, alertProfileEntities],
  (selectedId, entities): AlertProfile | null => (selectedId ? entities[selectedId] ?? null : null),
);

export const alertProfileOpen = (state: RootState) => state.alertProfile.open;
export const alertProfileTemplates = alertTemplateEntitySelectors.selectAll;
export const alertProfileOutputPluginTemplates = outputPluginTemplateEntitySelectors.selectAll;
